import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

// Import existing root reducer (temporary - will migrate to slices)
import rootReducer from "./rootReducer";
import { baseApi } from "./api/baseApi";
import { publicApi } from "./api/publicApi";
import { websocketMiddleware } from "./api/websocketMiddleware";

/**
 * Redux Persist Configuration
 *
 * Persistence strategy:
 * - Key: 'skypoint-studio-root' for sessionStorage key
 * - Version: 1 (increment when state shape changes)
 * - Whitelist: Only persist important state that should survive refreshes
 * - Blacklist: Exclude temporary UI state and large data that can be re-fetched
 */
const persistConfig = {
  key: "skypoint-studio-root",
  version: 1,
  storage,
  // Whitelist: Persist these reducers
  whitelist: [
    "notificationReducer", // Keep notifications
    "userPreferencesReducer", // Keep user preferences (timezone, dashboard settings)
    "reportTabsReducer", // Keep report tabs open across refreshes
  ],
  // Blacklist: Don't persist these reducers
  blacklist: [
    "tenantsReducer", // Fetched fresh on every page load; persisting causes stale rehydration bugs
    "authReducer", // Just trigger actions, no state to persist
    "tableReducer", // Temporary table state
    "dataflowReducer", // Large data, can be re-fetched
    "dashboardReducer", // Dashboard can reload
    "instanceHistoryReducer", // Large historical data
    "api", // RTK Query cache - will be refetched
  ],
};

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

/**
 * Configure Redux Store with Redux Toolkit
 *
 * Benefits of configureStore vs createStore:
 * - Includes redux-thunk by default
 * - Enables Redux DevTools automatically
 * - Includes middleware for immutability and serialization checks in development
 * - Simplifies store setup with good defaults
 */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types from redux-persist
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(baseApi.middleware, publicApi.middleware, websocketMiddleware),
  // DevTools enabled automatically in development, disabled in production
  devTools: process.env.NODE_ENV !== "production",
});

// Create persistor for PersistGate
export const persistor = persistStore(store);

/**
 * Type Exports for TypeScript Support
 *
 * Usage:
 * ```typescript
 * import { useAppSelector, useAppDispatch } from './store/hooks';
 *
 * const myState = useAppSelector((state) => state.someReducer.someValue);
 * const dispatch = useAppDispatch();
 * ```
 */
export type RootState = ReturnType<typeof store.getState>;
type AppDispatch = typeof store.dispatch;
